import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UploadCloud, ShieldCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import axios from "axios";

export default function ClientPortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/portal/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Portal not found"));
  }, [token]);

  const handleFiles = async (files) => {
    setBusy(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(`${API}/portal/${token}/upload`, fd);
        setUploads((u) => [{ name: f.name, size: r.data.size, ok: true }, ...u]);
      } catch (e) {
        setUploads((u) => [{ name: f.name, error: e?.response?.data?.detail || "Upload failed", ok: false }, ...u]);
      }
    }
    setBusy(false);
  };

  if (error) {
    return (
      <div data-testid="portal-error" className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center space-y-3">
          <AlertTriangle className="h-8 w-8 text-rose-500 mx-auto" />
          <div className="font-heading text-2xl text-slate-900 font-semibold">Portal unavailable</div>
          <div className="text-sm text-slate-600 border border-rose-200 bg-rose-50 rounded-md p-3">{error}</div>
          <div className="text-xs text-slate-500">Please contact your accountant for a new link.</div>
        </div>
      </div>
    );
  }

  if (!info) return <div className="min-h-screen grid place-items-center text-slate-500 text-sm">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-page">
      <div className="max-w-2xl mx-auto p-6 lg:p-10 space-y-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-slate-900 grid place-items-center">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading text-lg font-semibold text-slate-900">{info.practice_name}</div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Secure document portal</div>
          </div>
        </div>

        <div className="border border-slate-200 rounded-md bg-white p-6">
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Hello {info.client_display}</h1>
          <p className="mt-2 text-sm text-slate-600">
            Please upload the following documents. Your files are encrypted in transit and at rest — only your accountant can access them.
          </p>
          <ul className="mt-4 space-y-2">
            {info.docs_requested?.map((d, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-800">
                <span className="tx-dot bg-slate-400" /> {d}
              </li>
            ))}
          </ul>

          <label
            data-testid="portal-drop-area"
            className={`mt-6 block border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${busy ? "bg-slate-100 border-slate-300" : "border-slate-300 hover:bg-slate-50"}`}
          >
            <UploadCloud className="h-8 w-8 text-slate-400 mx-auto" />
            <div className="mt-2 text-sm text-slate-700 font-medium">
              {busy ? "Uploading…" : "Click or drop files here"}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              PDF, JPG, PNG or HEIC · max 10 MB per file · 50 MB per session
            </div>
            <input
              data-testid="portal-file-input"
              type="file"
              multiple
              className="hidden"
              accept="application/pdf,image/jpeg,image/png,image/heic,image/heif"
              onChange={(e) => e.target.files && handleFiles(Array.from(e.target.files))}
            />
          </label>

          {uploads.length > 0 && (
            <ul data-testid="portal-upload-list" className="mt-4 border border-slate-200 rounded-md divide-y divide-slate-100">
              {uploads.map((u, i) => (
                <li key={i} className="px-4 py-2 text-sm flex items-center justify-between">
                  <div className="min-w-0 flex items-center gap-2">
                    {u.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0" />}
                    <span className="truncate">{u.name}</span>
                  </div>
                  <div className={`text-xs shrink-0 ml-3 ${u.ok ? "text-slate-500" : "text-rose-600"}`}>
                    {u.ok ? `${(u.size / 1024).toFixed(1)} KB · uploaded` : u.error}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="text-xs text-slate-500 text-center">
          Link expires {new Date(info.expires_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} ·
          Your accountant will be notified as soon as you upload · <span className="font-mono">{info.already_uploaded_count}</span> file(s) already received
        </div>
      </div>
    </div>
  );
}
